import { useState, useEffect } from 'react';

import { PersonaId } from '../../personas';
import { PERSONA_MOCKS } from '../persona-mocks';
import * as styled from '../styles/earnings-calculator';

type Props = {
  personaId: PersonaId;
  onWaitlistClick: () => void;
};

const CLIENTS_MIN = 1;
const CLIENTS_MAX = 20;
const CLIENTS_STEP = 1;
const MONTHS_PER_YEAR = 12;

const formatNumber = (value: number): string =>
  new Intl.NumberFormat().format(value);

export const EarningsCalculatorCard = ({
  personaId,
  onWaitlistClick,
}: Props) => {
  const mock = PERSONA_MOCKS[personaId];
  const [avgPrice, setAvgPrice] = useState<number>(mock.avgBookingPrice);
  const [extraClients, setExtraClients] = useState<number>(
    mock.defaultExtraClients,
  );

  useEffect(() => {
    setAvgPrice(mock.avgBookingPrice);
    setExtraClients(mock.defaultExtraClients);
  }, [mock.avgBookingPrice, mock.defaultExtraClients]);

  const annualExtra = avgPrice * extraClients * MONTHS_PER_YEAR;

  return (
    <styled.CalculatorCard>
      <styled.CardTitle>How much could you earn?</styled.CardTitle>

      <styled.PriceField>
        <styled.SliderLabel>
          Your typical price per booking
        </styled.SliderLabel>
        <styled.PriceInputWrap>
          <styled.PriceInput
            type="number"
            inputMode="numeric"
            min={0}
            value={avgPrice}
            onChange={(event) =>
              setAvgPrice(Math.max(0, Number(event.target.value) || 0))
            }
            aria-label="Average booking price in your local currency"
          />
        </styled.PriceInputWrap>
        <styled.FieldHint>In your local currency.</styled.FieldHint>
      </styled.PriceField>

      <styled.SliderRow>
        <styled.SliderLabelRow>
          <styled.SliderLabel>Extra clients / month</styled.SliderLabel>
          <styled.SliderValue>{extraClients}</styled.SliderValue>
        </styled.SliderLabelRow>
        <styled.RangeInput
          min={CLIENTS_MIN}
          max={CLIENTS_MAX}
          step={CLIENTS_STEP}
          value={extraClients}
          onChange={(event) => setExtraClients(Number(event.target.value))}
          aria-label="Extra clients per month"
        />
        <styled.SliderRange>
          <span>{CLIENTS_MIN}</span>
          <span>{CLIENTS_MAX}</span>
        </styled.SliderRange>
      </styled.SliderRow>

      <styled.Divider />

      <styled.OutcomeLabel>You'd earn an extra</styled.OutcomeLabel>
      <styled.OutcomeNumber>{formatNumber(annualExtra)}</styled.OutcomeNumber>
      <styled.OutcomeCaption>per year</styled.OutcomeCaption>

      <styled.WaitlistCta type="button" onClick={onWaitlistClick}>
        → Claim your profile
      </styled.WaitlistCta>
    </styled.CalculatorCard>
  );
};
